/* @HEADER
 * @version 1.1.858 | 2026-10-24
 * @purpose ZVX-DEV-8226 — when the project this window stands on was deleted on another device: decide where the window goes (the organisation's next project, else a project outside it so the empty-organisation screen shows), tell the person unless they deleted it themselves, and ask the host to move.
 * @sidecar moveOffRemovedProject.ts.header.md
 * @layer lib | @hex _none_ | @ctx project
 * @public true
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8226

import { readAccountIdentity } from '../features/account/public-api'
import { chooseProjectAfterRemoteDelete } from './chooseProjectAfterRemoteDelete.ts'
import { resolvePersonalWorkspaceId, resolveProjectOrgId, type OrgScopeProject } from './projectOrgScope.ts'
import type { ProjectDeletedNoticeStore } from '../app/ProjectDeletedBanner.tsx'
import { removalKey, removalNamesPointer, type ProjectRemoval, type ProjectRemovalLedger } from './projectRemovalLedger.ts'
import { getAllProjects, getActiveProjectId, type ProjectRecord } from './projectStore.ts'

type Candidate = OrgScopeProject & { id: string; systemKind?: string }

export interface MovePlan {
  removal: ProjectRemoval
  /** The organisation's next project, or null when it has none left. */
  targetId: string | null
  /**
   * Only when `targetId` is null: a project OUTSIDE the organisation to stand on, so the screen the app already has for an
   * organisation with no project (ZVX-DEV-5483) is what the person sees, with the banner over it. The personal project is
   * preferred; it is where an empty organisation's person lands today. Null when the device holds nothing else.
   */
  outsideId: string | null
  /** False when the person deleted it themselves: the window still moves, with no banner. */
  announce: boolean
}

export interface MovePlanInput {
  removals: readonly ProjectRemoval[]
  handled: ReadonlySet<string>
  /** What this window's pointer names. */
  pointerId: string
  /** The projects that are live and visible to this person now, in registry order. */
  projects: readonly Candidate[]
  personalWorkspaceId: string | undefined
}

export function planMoveOffRemovedProject(input: MovePlanInput): MovePlan | null {
  const { removals, handled, pointerId, projects, personalWorkspaceId } = input
  const removal = removals.find((r) => !handled.has(removalKey(r)) && removalNamesPointer(r, pointerId))
  if (!removal) return null
  const targetId = chooseProjectAfterRemoteDelete({
    removedId: removal.id,
    organisationId: removal.organisationId,
    orderedIds: removal.orderedIds,
    projects,
    alsoRemovedIds: removals.filter((r) => r !== removal).map((r) => r.id),
    personalWorkspaceId,
  })
  let outsideId: string | null = null
  if (targetId === null) {
    const outside = projects.filter((p) => resolveProjectOrgId(p, personalWorkspaceId) !== removal.organisationId)
    outsideId = (outside.find((p) => p.systemKind === 'personal-home') ?? outside[0])?.id ?? null
  }
  return { removal, targetId, outsideId, announce: !removal.own }
}

/** What the move needs from the host: the switch, and the two things the host keeps for the life of the window. */
export interface MoveHost {
  switchProject(project: ProjectRecord): void | Promise<void>
  removalLedger: ProjectRemovalLedger
  deletedNotice: ProjectDeletedNoticeStore
}

/**
 * Looks at the ledger for a removal this window stands on and acts on it once: marks it handled, sets the notice, and asks the
 * host to move. Everything but the move itself happens before this returns, so a caller with nothing to do (`null`) carries on
 * in the same tick as it always did. Called at the end of every registry cycle and when another window writes the ledger.
 */
export function startMoveOffRemovedProject(host: MoveHost): Promise<void> | null {
  const { removalLedger, deletedNotice } = host
  const removals = removalLedger.read()
  if (removals.length === 0) return null
  const identity = readAccountIdentity()
  const personalWorkspaceId = resolvePersonalWorkspaceId(identity?.workspaces)
  const projects = getAllProjects()
  const plan = planMoveOffRemovedProject({
    removals, handled: removalLedger.handled(), pointerId: getActiveProjectId(), projects, personalWorkspaceId,
  })
  if (!plan) return null
  removalLedger.markHandled(plan.removal)
  const next = projects.find((p) => p.id === (plan.targetId ?? plan.outsideId)) ?? null
  if (plan.announce) {
    const organisation = identity?.workspaces?.find((w) => w.id === plan.removal.organisationId)
    deletedNotice.set({
      removedName: plan.removal.name || null,
      openedName: plan.targetId !== null ? (next?.name ?? null) : null,
      organisationName: organisation?.name ?? null,
    })
  }
  return Promise.resolve(next ? host.switchProject(next) : undefined)
}
